import React from 'react';
import {
  normalizeMembershipTypePricing,
  formatMembershipTypeOptionLabel,
  getMembershipFieldRequiredForContext,
} from '../utils/membership';
import { useSiteMembershipDefaults } from '../utils/siteDefaults';
import MembershipTypeInfo from './MembershipTypeInfo';
import { START_YEAR_FIELD, asksStartYear, typesWithoutStartYear } from '../utils/playerTypes';

function MembershipPortalScreen({ API, clubRef, intent = 'apply', currentUser, onDone }) {
  const siteDefaults = useSiteMembershipDefaults();
  const [meta, setMeta] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [form, setForm] = React.useState({});
  const [renewalYear, setRenewalYear] = React.useState(() => new Date().getFullYear() + 1);
  const [honeypotWebsite, setHoneypotWebsite] = React.useState('');
  const [alreadyMemberChoice, setAlreadyMemberChoice] = React.useState(intent === 'renew' ? 'yes' : 'no');
  const [accountMode, setAccountMode] = React.useState('silent-login');
  const [submitNotice, setSubmitNotice] = React.useState('');

  React.useEffect(() => {
    if (!clubRef) return;
    setLoading(true);
    fetch(`${API}/api/auth/clubs/${encodeURIComponent(clubRef)}/membership-form/public`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Failed to load form');
        return data;
      })
      .then((data) => {
        setMeta(data || null);
        setError('');
      })
      .catch((err) => {
        setMeta(null);
        setError(err.message || 'Failed to load form');
      })
      .finally(() => setLoading(false));
  }, [API, clubRef]);

  const fields = React.useMemo(() => (
    (meta && meta.form && Array.isArray(meta.form.fields)) ? meta.form.fields : []
  ), [meta]);
  const membershipTypeOptions = (meta && Array.isArray(meta.membershipTypeOptions)) ? meta.membershipTypeOptions : [];
  const membershipTypePricing = normalizeMembershipTypePricing(meta && meta.membershipTypePricing, membershipTypeOptions);
  const playerTypeOptions = (meta && Array.isArray(meta.playerTypeOptions)) ? meta.playerTypeOptions : [];
  /* What is shown and checked: every field, less "Bowling Start Year" for a
     player type the club does not ask it of. The prefill below still uses
     every field, so switching to a type that does ask finds the year there. */
  const startYearOff = !asksStartYear(typesWithoutStartYear(meta && meta.playerTypesWithoutStartYear, playerTypeOptions), form.playerType);
  const visibleFields = fields.filter(f => !(startYearOff && f.key === START_YEAR_FIELD));
  /* Platform-wide, not per club - see AuthScreen. */
  const ageGroupOptions = (meta && Array.isArray(meta.ageGroupOptions) && meta.ageGroupOptions.length)
    ? meta.ageGroupOptions
    : siteDefaults.ageGroupOptions;
  const enabled = !!(meta && meta.form && meta.form.enabled);
  const isRenewal = intent === 'renew';
  const isPublicGuestFlow = !currentUser;
  const effectiveRequiredForField = (field) => getMembershipFieldRequiredForContext(field, isRenewal);
  const renewalDueAt = currentUser ? currentUser.membershipRenewalDueAt : null;
  const renewalBaseYear = renewalDueAt
    ? new Date(renewalDueAt).getFullYear() + 1
    : new Date().getFullYear() + 1;
  const renewalYearOptions = Array.from({ length: 4 }, (_, index) => renewalBaseYear + index);

  React.useEffect(() => {
    if (isRenewal && Number.isInteger(renewalBaseYear)) setRenewalYear(renewalBaseYear);
  }, [isRenewal, renewalDueAt, renewalBaseYear]);

  const optionsForField = (field) => {
    if (!field || field.type !== 'select') return [];
    if (Array.isArray(field.options) && field.options.length > 0) return field.options;
    if (field.optionsSource === 'membershipTypeOptions') return membershipTypeOptions;
    if (field.optionsSource === 'playerTypeOptions') return playerTypeOptions;
    if (field.optionsSource === 'ageGroupOptions') return ageGroupOptions;
    return [];
  };

  const valueForTypedField = (field, value) => {
    if (field && field.type === 'boolean') return !!value;
    return value || '';
  };

  const updateTypedFieldValue = (field, rawValue) => {
    const nextValue = field && field.type === 'boolean' ? !!rawValue : rawValue;
    setForm(f => ({ ...f, [field.key]: nextValue }));
  };

  React.useEffect(() => {
    if (!isRenewal || !currentUser || fields.length === 0) return;
    setForm(previous => {
      if (Object.keys(previous).length > 0) return previous;
      const storedExtra = currentUser.membershipExtraData && typeof currentUser.membershipExtraData === 'object' && !Array.isArray(currentUser.membershipExtraData)
        ? currentUser.membershipExtraData
        : {};
      const populated = {};
      fields.forEach(field => {
        if (field.type === 'content') return;
        if (field.key === 'fullName') populated[field.key] = currentUser.name || '';
        else if (field.key === 'email') populated[field.key] = currentUser.email || '';
        else if (field.key === 'phone') populated[field.key] = currentUser.phone || '';
        else if (field.key === 'membershipType') populated[field.key] = currentUser.membershipType || '';
        else if (field.key === 'playerType') populated[field.key] = currentUser.playerType || '';
        else if (field.key === 'yearsPlaying') populated[field.key] = currentUser.bowlingStartYear || '';
        else if (Object.prototype.hasOwnProperty.call(storedExtra, field.key)) populated[field.key] = storedExtra[field.key];
      });
      return populated;
    });
  }, [isRenewal, currentUser, fields]);

  /**
   * What will actually be sent for a field.
   *
   * Name and email are the signed-in account's, not the form's: they are
   * shown as disabled boxes filled from the account, so nothing ever types
   * into the form state behind them. The payload has always known that and
   * put the account's values in — the check above it did not, so a member
   * applying while signed in was told "Full Name is required" about a box
   * with their name already in it, and could not get past it.
   */
  const submittedValue = (field) => {
    if (currentUser && field.key === 'fullName') return currentUser.name || '';
    if (currentUser && field.key === 'email') return currentUser.email || '';
    return form[field.key];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitNotice('');

    for (const field of visibleFields) {
      if (field.type === 'content') continue;
      const rawValue = submittedValue(field);
      const v = field.type === 'boolean'
        ? (rawValue === true || rawValue === false ? String(rawValue) : '')
        : String(rawValue || '').trim();
      if (effectiveRequiredForField(field) && !v) {
        setError(`${field.label} is required`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        data: {
          ...form,
          ...(currentUser ? { fullName: currentUser.name || '', email: currentUser.email || '' } : {}),
        },
      };

      let r;
      if (currentUser && isRenewal) {
        r = await fetch(`${API}/api/auth/membership/renew/me`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, renewalYear }),
        });
      } else {
        const effectiveRenew = isRenewal || alreadyMemberChoice === 'yes';
        r = await fetch(`${API}/api/auth/membership/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clubId: meta && meta.club ? meta.club.id : null,
            data: payload.data,
            website: honeypotWebsite,
            intent: effectiveRenew ? 'renew' : 'apply',
            alreadyMember: effectiveRenew,
            ...(effectiveRenew ? { renewalYear } : {}),
            accountMode,
          }),
        });
      }

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Failed to submit form');
      if (isPublicGuestFlow && accountMode === 'silent-login') {
        if (data.accountSetupSent) {
          setSubmitNotice('A GreenSheet login setup email has been sent. Use it to set your password.');
        } else if (data.accountSetupError) {
          setSubmitNotice(`Form submitted, but login setup was not completed: ${data.accountSetupError}`);
        }
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit form');
    }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)', padding: '36px 16px' }}>
      <div className="page-container-md">
        <div className="w3-card w3-round w3-white" style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0 }}>
                <i className={`fa ${isRenewal ? 'fa-refresh' : 'fa-id-card'} w3-margin-right`}></i>
                {isRenewal ? 'Membership Renewal' : 'Membership Application'}
              </h3>
              {meta && meta.club && <p style={{ margin: '4px 0 0', opacity: 0.85 }}>Club: {meta.club.name}</p>}
            </div>
            {!!(meta && meta.club && meta.club.logo) && (
              <img
                src={meta.club.logo}
                alt={`${meta.club.name || 'Club'} logo`}
                style={{ width: '78px', height: '78px', objectFit: 'contain', borderRadius: '8px', background: 'rgba(255,255,255,0.14)', padding: '6px', flexShrink: 0 }}
              />
            )}
          </div>

          <div className="w3-container w3-padding-16">
            {error && <div className="w3-panel w3-red w3-round w3-padding-small"><small>{error}</small></div>}

            {success ? (
              <div>
                <div className="w3-panel w3-pale-green w3-leftbar w3-border-green w3-round">
                  <strong><i className="fa fa-check-circle w3-margin-right"></i>{isRenewal ? 'Renewal submitted' : 'Application submitted'}</strong>
                  <p className="w3-small" style={{ marginBottom: 0 }}>Your {isRenewal ? 'renewal' : 'membership'} is pending until a club admin confirms it.</p>
                  {submitNotice && <p className="w3-small" style={{ marginTop: '8px', marginBottom: 0 }}>{submitNotice}</p>}
                </div>
                <button className="w3-button w3-theme w3-round" onClick={onDone}>
                  {currentUser ? 'Back to dashboard' : 'Back'}
                </button>
              </div>
            ) : loading ? (
              <p className="w3-text-grey"><i className="fa fa-spinner fa-spin w3-margin-right"></i>Loading form...</p>
            ) : !enabled ? (
              <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-amber w3-round">
                This club is not accepting online membership submissions right now.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {meta && meta.form && meta.form.introText && <p className="w3-small w3-text-grey">{meta.form.introText}</p>}
                {currentUser && (
                  <p className="w3-small w3-text-grey">
                    Signed in as <strong>{currentUser.name}</strong> ({currentUser.email})
                  </p>
                )}
                {isRenewal && (
                  <div className="w3-margin-bottom">
                    <label className="w3-small w3-text-grey"><strong>Renewal Year</strong></label>
                    <select
                      className="w3-select w3-border w3-round"
                      value={renewalYear}
                      onChange={e => setRenewalYear(parseInt(e.target.value, 10))}
                    >
                      {renewalYearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                    <p className="w3-small w3-text-grey" style={{ margin: '6px 0 0' }}>
                      Your membership will expire on the club&apos;s renewal date in this year.
                    </p>
                  </div>
                )}
                {!currentUser && !isRenewal && (
                  <div className="w3-margin-bottom">
                    <label className="w3-small w3-text-grey"><strong>Already a member at this club?</strong></label>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="radio"
                          name="portalAlreadyMember"
                          checked={alreadyMemberChoice === 'yes'}
                          onChange={() => setAlreadyMemberChoice('yes')}
                        />
                        <span className="w3-small">Yes, renew my existing profile</span>
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="radio"
                          name="portalAlreadyMember"
                          checked={alreadyMemberChoice === 'no'}
                          onChange={() => setAlreadyMemberChoice('no')}
                        />
                        <span className="w3-small">No, I am new</span>
                      </label>
                    </div>
                    {alreadyMemberChoice === 'yes' && (
                      <p className="w3-small w3-text-grey" style={{ margin: '6px 0 0' }}>
                        Use the same email as your existing profile so we renew the correct member record.
                      </p>
                    )}
                  </div>
                )}

                {visibleFields.map(field => {
                  const value = valueForTypedField(field, form[field.key]);
                  const options = optionsForField(field);
                  const fieldRequired = effectiveRequiredForField(field);
                  const isIdentityField = (field.key === 'fullName' || field.key === 'email') && !!currentUser;

                  return (
                    <div key={field.key} className="w3-margin-bottom">
                      {field.type === 'content' ? (
                        <div style={{ margin: '4px 0 10px' }}>
                          {field.label ? <p className="w3-small" style={{ margin: '0 0 4px', fontWeight: 700 }}>{field.label}</p> : null}
                          {field.detail ? <p className="w3-small w3-text-grey" style={{ margin: 0, whiteSpace: 'pre-line' }}>{field.detail}</p> : null}
                        </div>
                      ) : (
                        <>
                      <label className="w3-small w3-text-grey">{field.label}{fieldRequired ? ' *' : ''}</label>
                      {isIdentityField ? (
                        <input className="w3-input w3-border w3-round" value={field.key === 'fullName' ? (currentUser.name || '') : (currentUser.email || '')} disabled />
                      ) : field.type === 'textarea' ? (
                        <textarea
                          className="w3-input w3-border w3-round"
                          rows={3}
                          value={value}
                          required={fieldRequired}
                          onChange={e => updateTypedFieldValue(field, e.target.value)}
                        />
                      ) : field.type === 'select' ? (
                        <>
                        <select
                          className="w3-select w3-border w3-round"
                          value={value}
                          required={fieldRequired}
                          onChange={e => updateTypedFieldValue(field, e.target.value)}>
                          <option value="">Select...</option>
                          {options.map(opt => <option key={opt} value={opt}>{field.optionsSource === 'membershipTypeOptions' ? formatMembershipTypeOptionLabel(opt, membershipTypePricing) : opt}</option>)}
                        </select>
                        {field.optionsSource === 'membershipTypeOptions' && (
                          <MembershipTypeInfo type={value}
                            descriptions={meta && meta.membershipTypeDescriptions}
                            pricing={membershipTypePricing} />
                        )}
                        </>
                      ) : field.type === 'boolean' ? (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                          <input
                            type="checkbox"
                            checked={!!value}
                            required={fieldRequired}
                            onChange={e => updateTypedFieldValue(field, e.target.checked)}
                          />
                          <span className="w3-small">Yes</span>
                        </label>
                      ) : field.type === 'number' ? (
                        <input
                          className="w3-input w3-border w3-round"
                          type="number"
                          value={value}
                          required={fieldRequired}
                          onChange={e => updateTypedFieldValue(field, e.target.value)}
                        />
                      ) : field.type === 'date' ? (
                        <input
                          className="w3-input w3-border w3-round"
                          type="date"
                          value={value}
                          required={fieldRequired}
                          onChange={e => updateTypedFieldValue(field, e.target.value)}
                        />
                      ) : (
                        <input
                          className="w3-input w3-border w3-round"
                          type={field.type === 'email' ? 'email' : 'text'}
                          value={value}
                          required={fieldRequired}
                          onChange={e => updateTypedFieldValue(field, e.target.value)}
                        />
                      )}
                        </>
                      )}
                    </div>
                  );
                })}

                {!currentUser && (
                  <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                    <label>Website</label>
                    <input
                      type="text"
                      autoComplete="off"
                      tabIndex={-1}
                      value={honeypotWebsite}
                      onChange={e => setHoneypotWebsite(e.target.value)}
                    />
                  </div>
                )}

                {!currentUser && (
                  <div className="w3-margin-bottom">
                    <label className="w3-small w3-text-grey"><strong>To manage your account, create a login and password?</strong></label>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="radio"
                          name="portalAccountMode"
                          checked={accountMode === 'silent-login'}
                          onChange={() => setAccountMode('silent-login')}
                        />
                        <span className="w3-small">Yes, create a login and password</span>
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="radio"
                          name="portalAccountMode"
                          checked={accountMode === 'guest'}
                          onChange={() => setAccountMode('guest')}
                        />
                        <span className="w3-small">No, continue as guest only</span>
                      </label>
                    </div>
                    {accountMode === 'silent-login' && (
                      <p className="w3-small w3-text-grey" style={{ margin: '6px 0 0' }}>
                        We will email you a secure link to create your password after you submit.
                      </p>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button className="w3-button w3-theme w3-round" type="submit" disabled={submitting}>
                    {submitting ? <><i className="fa fa-spinner fa-spin w3-margin-right"></i>Submitting...</> : <><i className="fa fa-paper-plane w3-margin-right"></i>{(isRenewal || alreadyMemberChoice === 'yes') ? 'Submit Renewal' : 'Submit Application'}</>}
                  </button>
                  <button className="w3-button w3-light-grey w3-round" type="button" onClick={onDone}>Cancel</button>
                </div>
                {/* Said before the details are sent, not after: the Privacy Act asks that
                    people are told what happens to their information when it is collected. */}
                <p className="privacy-notice">
                  Your club uses these details to manage your membership; some answers are emailed to the club and not stored. Only questions marked * are required.{' '}
                  <a href="/privacy.html" target="_blank" rel="noopener">How we look after your information</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MembershipPortalScreen;
